import '@fontsource-variable/archivo';
import './theme.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ApiClient } from './api';
import { App } from './App';
import { nativeSignIn, startAuth } from './auth';
import { Intro, INTRO_MS, INTRO_REDUCED_MS } from './components/Intro';
import type { Me, ShellConfig } from './types';

const root = createRoot(document.getElementById('root')!);

/**
 * Pre-auth screens. These are the first and last thing anyone sees on a
 * bad day, and they used to be a line of grey text on black. They now
 * carry the same wordmark and surface as the Shell behind them, so the
 * sign-in hop reads as one product rather than three different pages.
 */
function Splash({ children, tone }: { children: React.ReactNode; tone?: 'error' }) {
  return (
    <div className="splash">
      <div className="splash-card">
        <div className="wordmark">
          Addaiaspora<span className="dot">.</span>
          <small>A.C.C.R.A · Addaiaspora Command Centre for Real-time Analytics</small>
        </div>
        <p className={tone === 'error' ? 'splash-note splash-note-error' : 'splash-note'}>
          {children}
        </p>
      </div>
    </div>
  );
}

/**
 * The drawn A opens the dashboard on the web as it opens the iPhone app
 * (Matthew, 1 November 2026: app and website always match). Inside the app
 * the native intro has already played, so the web one is skipped there.
 * The dashboard appears once both the intro and sign-in are done.
 */
const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
const introDone = nativeSignIn()
  ? Promise.resolve()
  : new Promise<void>((resolve) => window.setTimeout(resolve, reducedMotion ? INTRO_REDUCED_MS : INTRO_MS));
root.render(nativeSignIn() ? <Splash>Signing you in…</Splash> : <Intro />);

// The service worker shows phone alerts and caches nothing (public/sw.js).
// Registered before sign-in so an installed app always has it.
if ('serviceWorker' in navigator) {
  void navigator.serviceWorker.register('/sw.js').catch(() => undefined);
}

async function boot() {
  const config = (await (await fetch('/config.json')).json()) as ShellConfig;
  const session = await startAuth(config);
  if (!session) return; // redirecting to Cognito

  const api = new ApiClient(session);
  const [me] = await Promise.all([api.get<Me>('/api/me'), introDone]);
  const mapStyleUrl = config.mapStyleUrl ?? 'https://tiles.openfreemap.org/styles/dark';

  root.render(
    <StrictMode>
      <App api={api} me={me} mapStyleUrl={mapStyleUrl} onSignOut={() => session.signOut()} />
    </StrictMode>,
  );
}

boot().catch((err) => {
  root.render(
    <Splash tone="error">
      The dashboard could not start: {(err as Error).message}. The platform admins remain reachable
      directly; this surface is monitoring only.
    </Splash>,
  );
});
